/**
 * useDriverPlannedRoute — ruta optimizada PLANIFICADA del repartidor con sesión activa.
 *
 * Fuente: GET driver-route-sessions/by-driver/:driverId/active (microservice-routes).
 * La geometría la calcula y persiste el worker de optimización server-side con el
 * MISMO motor que usa la app del repartidor, y cubre SOLO el tramo pendiente
 * ("lo que le queda por recorrer"); el backend la refresca en cada re-optimización
 * de la sesión (inicio de ruta / paquetes agregados). Si el repartidor reordena
 * paradas a mano en su app, la traza puede diferir hasta la próxima re-optimización.
 *
 * Se refresca cada REFRESH_MS mientras el panel está abierto: así la traza sigue a
 * las re-optimizaciones del backend y desaparece cuando la sesión se cierra (404).
 *
 * Devuelve:
 *  - `plannedCoords`: array [[lat, lng]] apto para <Polyline> de Leaflet, o null cuando no hay
 *    nada que pintar (sin sesión activa, sin geometría, driverId inválido/null).
 *  - `nextStops`: las paradas PENDIENTES en el orden real que va a seguir, numeradas. Sale de
 *    `orderedStops` (lo publica el teléfono, incluye las paradas sin paquete) y cae a
 *    `orderedStopIds` en sesiones viejas calculadas por el worker, que solo conoce paquetes y
 *    no trae coordenadas ni etiquetas.
 *  - `stopEtas`: hora estimada de llegada POR PARADA, indexada por `stopId`. La calcula el
 *    backend contra la posición viva del repartidor, no contra la agenda de la optimización:
 *    una agenda estática no sabe si va atrasado. Puede faltar para paradas concretas (ver
 *    `stop-etas.service.ts`), y ahí la parada simplemente no aparece en el índice.
 *  - `etaMeta`: cuándo se calculó, qué tan vieja era la posición usada, y por qué no hay ETA si
 *    no lo hay. La frescura se muestra siempre: un ETA sin fecha de cálculo es el que después
 *    nadie sabe si creer.
 *
 * Un 404 es el caso normal "sin ruta activa", no un error.
 */
import { useEffect, useState } from 'react';
import {
  getDriverActiveRouteSession,
  getDriverStopEtas,
} from '../../../api/routes';
import { decodePolyline6 } from '../../../utils/mapHelpers';

const REFRESH_MS = 60_000;

export function useDriverPlannedRoute(driverId) {
  const [plannedCoords, setPlannedCoords] = useState(null);
  // Paradas PENDIENTES en el orden real que el repartidor va a seguir. Es lo que contesta
  // "en qué orden visita lo que le queda"; la traza sola no lo dice.
  const [nextStops, setNextStops] = useState(null);
  const [stopEtas, setStopEtas] = useState(null);
  const [etaMeta, setEtaMeta] = useState(null);

  useEffect(() => {
    let alive = true;
    setPlannedCoords(null);
    setNextStops(null);
    setStopEtas(null);
    setEtaMeta(null);
    if (!/^[a-f0-9]{24}$/i.test(String(driverId || ''))) return undefined;

    const loadEtas = () => {
      getDriverStopEtas(driverId)
        .then((res) => {
          if (!alive) return;
          const byStopId = {};
          for (const s of res?.stops ?? []) {
            // Solo entran las paradas que SÍ tienen hora. El resto queda fuera del índice y la
            // lista las muestra sin ETA, que es distinto de mostrarlas con una hora inventada.
            if (s?.stopId && s?.etaAt) byStopId[s.stopId] = s.etaAt;
          }
          setStopEtas(byStopId);
          setEtaMeta({
            calculatedAt: res?.calculatedAt ?? null,
            positionAsOf: res?.positionAsOf ?? null,
            unavailableReason: res?.unavailableReason ?? null,
          });
        })
        .catch((err) => {
          if (!alive) return;
          if (err?.response?.status === 404) {
            setStopEtas(null);
            setEtaMeta(null);
            return;
          }
          // A diferencia de la traza, acá NO se conserva el último valor conocido: una hora vieja
          // presentada como actual es peor que ninguna, y el consumidor ya sabe mostrar la lista
          // sin ETA. Se limpia la meta para que tampoco quede una frescura que ya no corresponde.
          setStopEtas(null);
          setEtaMeta(null);
          console.warn('No se pudo cargar el ETA por parada del repartidor', err);
        });
    };

    const load = () => {
      getDriverActiveRouteSession(driverId)
        .then((session) => {
          if (!alive) return;
          const opt = session?.optimizedRoute;
          const coords =
            opt?.status === 'READY' && opt?.encodedPolyline
              ? decodePolyline6(opt.encodedPolyline)
              : [];
          // null limpia una traza previa si la sesión perdió la geometría.
          setPlannedCoords(coords.length >= 2 ? coords : null);

          // `orderedStops` lo publica el teléfono e incluye las paradas agregadas a mano
          // (sin paquete). Se cae a `orderedStopIds` para las sesiones que todavía calculó
          // el worker server-side, que solo conoce paquetes y no trae coordenadas.
          const ready = opt?.status === 'READY';
          const published = Array.isArray(opt?.orderedStops) ? opt.orderedStops : null;
          if (ready && published) {
            const pending = published
              .filter((st) => st && st.status !== 'delivered' && st.status !== 'failed')
              .map((st, i) => ({
                order: i + 1,
                stopId: st.stopId,
                packageId: st.packageId ?? null,
                label: st.label ?? null,
                lat: st.lat,
                lng: st.lng,
              }));
            setNextStops(pending);
          } else if (ready && Array.isArray(opt?.orderedStopIds)) {
            const statuses = opt?.stopStatuses ?? {};
            setNextStops(
              opt.orderedStopIds
                .filter((id) => statuses[id] !== 'delivered' && statuses[id] !== 'failed')
                .map((id, i) => ({
                  order: i + 1,
                  stopId: id,
                  packageId: id,
                  label: null,
                  lat: null,
                  lng: null,
                })),
            );
          } else {
            setNextStops(null);
          }
        })
        .catch((err) => {
          if (!alive) return;
          if (err?.response?.status === 404) {
            // Sin ruta activa (caso normal, p.ej. la sesión se completó): limpiar.
            setPlannedCoords(null);
            setNextStops(null);
            return;
          }
          // Capa opcional: ante errores transitorios se conserva la última traza
          // conocida (el mapa sigue funcionando), pero dejamos rastro en consola.
          console.warn('No se pudo cargar la ruta planificada del repartidor', err);
        });
    };

    load();
    loadEtas();
    const timer = setInterval(() => {
      load();
      loadEtas();
    }, REFRESH_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [driverId]);

  return { plannedCoords, nextStops, stopEtas, etaMeta };
}
